import { useCallback, useEffect, useRef, useState } from "react";
import { Car, Mic, Volume2, Loader2, SkipForward, Square, Play, CheckCircle2, AlertTriangle } from "lucide-react";
import { http, errMsg, DOMAIN_SHORT } from "@/lib/api";
import { useHandsFree } from "@/hooks/useHandsFree";

const ANSWER_MS = 3 * 60 * 1000;
const SILENCE_MS = 4000;
const SKIP_RE = /^\W*(skip|next|pass)(\s+(it|this|question))?\W*$/i;
const STOP_RE = /^\W*(stop|end|finish|quit)(\s+(driving( mode)?|session|now))?\W*$/i;

const STATE_LABEL = {
  intro: ["Getting ready", Volume2],
  speaking: ["Reading the question", Volume2],
  listening: ["Listening — answer out loud", Mic],
  marking: ["Marking your answer", Loader2],
  feedback: ["Feedback", Volume2],
};

const Setup = ({ domain, setDomain, stats, onStart, error }) => (
  <div className="max-w-2xl mx-auto px-4 py-10 fade-up">
    <p className="font-mono text-xs tracking-[0.2em] uppercase" style={{ color: "var(--accent)" }}>Hands-free</p>
    <h1 className="mt-2 font-display text-4xl sm:text-5xl font-extrabold tracking-tight flex items-center gap-3"><Car size={40} /> Driving mode</h1>
    <p className="mt-4 text-base" style={{ color: "var(--ink-2)" }}>
      Rapid-fire questions, each focused on one critical management point. The question is read aloud and the app listens automatically. You have up to 3 minutes per answer; pausing for about 4 seconds ends your answer. You then hear spoken feedback, including any must-say points you missed, and the next question starts on its own.
    </p>
    <ul className="mt-4 text-sm space-y-1" style={{ color: "var(--ink-2)" }}>
      <li>Say <strong>"Skip"</strong> to move to the next question.</li>
      <li>Say <strong>"Stop"</strong> to end the session.</li>
    </ul>
    <div className="mt-6 flex flex-wrap items-center gap-3">
      <label className="text-sm">Domain</label>
      <select data-testid="driving-domain-select" value={domain} onChange={(e) => setDomain(e.target.value)}
        className="h-11 rounded-lg px-3 text-sm" style={{ background: "var(--muted-bg)", border: "1px solid var(--line)", color: "var(--ink)" }}>
        <option value="">All domains ({stats?.total ?? "…"} questions)</option>
        {DOMAIN_SHORT.map((n, i) => <option key={i} value={i + 1} disabled={!stats?.by_domain?.[i + 1]}>D{i + 1} {n} ({stats?.by_domain?.[i + 1] ?? 0})</option>)}
      </select>
    </div>
    {error && <p data-testid="driving-error" className="mt-4 text-sm" style={{ color: "var(--alert)" }}>{error}</p>}
    <button data-testid="driving-start-button" onClick={onStart} disabled={!stats?.total}
      className="btn-primary mt-8 h-16 w-full sm:w-auto px-10 rounded-full text-lg font-bold flex items-center justify-center gap-3">
      <Play size={22} /> Start driving mode
    </button>
    <p className="mt-6 text-xs flex gap-2" style={{ color: "var(--ink-3)" }}>
      <AlertTriangle size={14} className="shrink-0" /> Set it up before you drive and keep your eyes on the road. Keep this screen open: most phones pause the mic and audio when the screen locks or you switch apps.
    </p>
  </div>
);

const Live = ({ q, idx, state, level, elapsed, last, onSkip, onStop }) => {
  const [label, Icon] = STATE_LABEL[state] || ["", Loader2];
  const remaining = Math.max(0, Math.ceil((ANSWER_MS - elapsed) / 1000));
  return (
    <div data-testid="driving-live" className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between">
        <span data-testid="driving-question-counter" className="font-mono text-sm" style={{ color: "var(--ink-3)" }}>Question {idx + 1}{q ? ` · D${q.domain} ${DOMAIN_SHORT[q.domain - 1]}` : ""}</span>
        {state === "listening" && <span data-testid="driving-answer-timer" className="font-mono text-2xl font-bold">{Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, "0")}</span>}
      </div>
      <div data-testid="driving-state" className="mt-6 flex items-center gap-3 text-xl font-display font-bold" style={{ color: state === "listening" ? "var(--alert)" : "var(--accent)" }}>
        <Icon size={28} className={state === "marking" ? "animate-spin" : ""} /> {label}
      </div>
      {state === "listening" && (
        <div className="mt-3 h-3 rounded-full overflow-hidden" style={{ background: "var(--muted-bg)" }}>
          <div data-testid="driving-mic-level" className="h-full rounded-full transition-[width] duration-100" style={{ width: `${Math.round(level * 100)}%`, background: "var(--alert)" }} />
        </div>
      )}
      {q && <p data-testid="driving-question-text" className="mt-6 font-display text-2xl sm:text-3xl font-semibold leading-snug">{q.question}</p>}
      {last && (
        <div data-testid="driving-last-feedback" className="mt-8 card p-5" style={state === "feedback" ? undefined : { opacity: 0.6 }}>
          <div className="flex justify-between gap-3 text-sm">
            <span className="font-semibold">{state === "feedback" ? "" : "Previous question: "}{last.verdict}</span>
            <span className="font-mono font-bold">{last.score}/10</span>
          </div>
          <p className="mt-2 text-sm" style={{ color: "var(--ink-2)" }}>{last.spoken_feedback}</p>
          {last.missed_must_say?.length > 0 && (
            <ul data-testid="driving-missed-list" className="mt-3 space-y-1 text-sm">
              {last.missed_must_say.map((m, i) => <li key={i} className="flex gap-2"><AlertTriangle size={14} className="mt-0.5 shrink-0" style={{ color: "var(--warn)" }} />{m}</li>)}
            </ul>
          )}
        </div>
      )}
      <div className="mt-10 grid grid-cols-2 gap-4">
        <button data-testid="driving-skip-button" onClick={onSkip} className="btn-ghost h-20 rounded-2xl text-lg font-bold flex items-center justify-center gap-2"><SkipForward size={24} /> Skip</button>
        <button data-testid="driving-stop-button" onClick={onStop} className="h-20 rounded-2xl text-lg font-bold flex items-center justify-center gap-2 text-white" style={{ background: "var(--alert)" }}><Square size={22} fill="currentColor" /> Stop</button>
      </div>
    </div>
  );
};

const Summary = ({ results, error, onRestart }) => {
  const answered = results.filter((r) => !r.skipped);
  const avg = answered.length ? Math.round(answered.reduce((a, r) => a + r.score, 0) / answered.length * 10) / 10 : null;
  return (
    <div data-testid="driving-summary" className="max-w-3xl mx-auto px-4 py-10 fade-up">
      <h1 className="font-display text-3xl font-bold">Session summary</h1>
      <p data-testid="driving-summary-stats" className="mt-2 text-sm" style={{ color: "var(--ink-3)" }}>
        {answered.length} answered · {results.length - answered.length} skipped{avg != null ? ` · average ${avg}/10` : ""}
      </p>
      {error && <p data-testid="driving-summary-error" className="mt-3 text-sm" style={{ color: "var(--alert)" }}>Session ended early: {error}</p>}
      <div className="mt-6 space-y-3">
        {results.map((r, i) => (
          <div key={i} data-testid={`driving-summary-item-${i}`} className="card p-4">
            <div className="flex justify-between gap-3">
              <p className="text-sm font-semibold">{r.q.question}</p>
              <span className="font-mono text-sm font-bold shrink-0">{r.skipped ? "Skipped" : `${r.score}/10`}</span>
            </div>
            {!r.skipped && <p className="mt-2 text-xs italic" style={{ color: "var(--ink-3)" }}>You said: {r.transcript || "(nothing heard)"}</p>}
            {r.missed_must_say?.length > 0 && <p className="mt-2 text-xs" style={{ color: "var(--warn)" }}>Missed: {r.missed_must_say.join("; ")}</p>}
            {r.must_say && <p className="mt-1 text-xs" style={{ color: "var(--ok)" }}><CheckCircle2 size={12} className="inline mr-1" />Must-say: {r.must_say.join("; ")}</p>}
          </div>
        ))}
      </div>
      <button data-testid="driving-restart-button" onClick={onRestart} className="btn-primary mt-8 h-12 px-8 rounded-full font-semibold">New session</button>
    </div>
  );
};

export default function Driving() {
  const hf = useHandsFree();
  const [stats, setStats] = useState(null);
  const [domain, setDomain] = useState("");
  const [phase, setPhase] = useState("setup");
  const [state, setState] = useState("");
  const [q, setQ] = useState(null);
  const [idx, setIdx] = useState(0);
  const [level, setLevel] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [last, setLast] = useState(null);
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const running = useRef(false);
  const skipFlag = useRef(false);
  const resultsRef = useRef([]);
  resultsRef.current = results;

  useEffect(() => { http.get("/driving/stats").then((r) => setStats(r.data)); }, []);
  useEffect(() => () => { running.current = false; hf.release(); }, [hf.release]); // eslint-disable-line react-hooks/exhaustive-deps

  const transcribe = async (blob) => {
    if (blob.size < 2000) return "";
    const fd = new FormData();
    fd.append("audio", blob, blob.type.includes("mp4") ? "answer.m4a" : "answer.webm");
    return (await http.post("/transcribe", fd)).data.text || "";
  };

  const loop = useCallback(async (questions) => {
    for (let i = 0; i < questions.length && running.current; i++) {
      const cur = questions[i];
      setQ(cur); setIdx(i); skipFlag.current = false;
      if (!running.current) break;
      setState("speaking");
      await hf.speak(`Question ${i + 1}. ${cur.question}`);
      if (!running.current) break;
      if (skipFlag.current) { setResults((r) => [...r, { q: cur, skipped: true }]); continue; }
      hf.beep();
      setElapsed(0);
      setState("listening");
      const { blob, spoke } = await hf.listen({ maxMs: ANSWER_MS, silenceMs: SILENCE_MS, onLevel: setLevel, onTick: setElapsed });
      if (!running.current) break;
      if (skipFlag.current) { setResults((r) => [...r, { q: cur, skipped: true }]); continue; }
      setState("marking");
      const text = spoke ? await transcribe(blob) : "";
      if (STOP_RE.test(text)) break;
      if (SKIP_RE.test(text)) {
        setResults((r) => [...r, { q: cur, skipped: true }]);
        await hf.speak("Skipping.");
        continue;
      }
      const { data } = await http.post("/driving/answer", { question_id: cur.id, transcript: text });
      setLast(data);
      setResults((r) => [...r, { q: cur, transcript: text, ...data }]);
      if (!running.current) break;
      setState("feedback");
      await hf.speak(data.spoken_feedback);
    }
    if (running.current) await hf.speak("Driving mode finished. Well done.").catch(() => {});
    running.current = false;
    hf.release();
    setPhase("summary");
  }, [hf]);

  const start = async () => {
    setError("");
    try {
      await hf.unlock();
      const { data } = await http.get("/driving/questions", { params: { count: 20, ...(domain ? { domain } : {}) } });
      setResults([]); setLast(null);
      running.current = true;
      setPhase("live");
      setState("intro");
      await hf.speak("Driving mode. Say skip to move on, or stop to finish.");
      await loop(data);
    } catch (e) {
      running.current = false;
      hf.release();
      setError(e?.name === "NotAllowedError" ? "Microphone access is needed for driving mode." : errMsg(e));
      setPhase(resultsRef.current.length ? "summary" : "setup");
    }
  };

  const skip = () => { skipFlag.current = true; hf.interrupt(); };
  const stop = () => { running.current = false; hf.interrupt(); };

  if (phase === "summary") return <Summary results={results} error={error} onRestart={() => { setError(""); setPhase("setup"); }} />;
  if (phase === "live") return <Live q={q} idx={idx} state={state} level={level} elapsed={elapsed} last={last} onSkip={skip} onStop={stop} />;
  return <Setup domain={domain} setDomain={setDomain} stats={stats} onStart={start} error={error} />;
}
